import React, { useState, useEffect, useRef } from 'react';
import { Alert, Avatar, Box, Button, Container, InputBase, LinearProgress, linearProgressClasses, Pagination, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography, Backdrop, Drawer, Link, TextField, Checkbox, FormControlLabel, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import AddUser from '../modals/users/AddUser';
import BuyLicense from '../modals/users/BuyLicense';
import ChangePasswordModal from '../modals/ChangePassword';
import { DeleteIconBlue, MdiTickIcon, ModalClose, PasswordIconBlue } from '../assets/icons';
import AuthService from '../services/auth.service';
import { CompanyUser } from '../types/auth';
import { formatDate, validateUserPermissions, validateCompanyId } from '../utils/common.util';
import { decodeMetadataToken } from '../utils/token.utils';
import Loader from './common/Loader';

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 4,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: "#DDE0E5",
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 16,
        backgroundColor: '#178EFC',
    },
}));

const BorderLinearProgressMobile = styled(LinearProgress)(({ theme }) => ({
    height: 6,
    borderRadius: 4,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: "#DDE0E5",
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 4,
        backgroundColor: '#178EFC',
    },
}));

const Users = () => {
    interface TabPanelProps {
        children?: React.ReactNode;
        index: number;
        value: number;
    }
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogOpenLicense, setDialogOpenLicnese] = useState(false);
    const [changePasswordDialog, setChangePasswordDialog] = useState(false);
    const [page, setPage] = useState(1);
    const [itemsPerPage] = useState(5);

    const [users, setUsers] = useState<CompanyUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // to hide the easyCRM  
    const [isEasyCRM, setIsEasyCRM] = React.useState(false);
    const [isPausedPlan, setIsPausedPlan] = useState(false);

    // Check if user is on paused plan
    useEffect(() => {
        const metadata = decodeMetadataToken();
        const isUserOnPausedPlan =
            metadata?.companies?.[0]?.subscription?.status === 'paused' ||
            metadata?.companies?.[0]?.subscription?.planId?.toLowerCase()?.includes('subscription-pause');
        setIsPausedPlan(isUserOnPausedPlan);
    }, []);

    const rows = users.map((user) => ({
        name: user.name,
        email: user.email,
        phoneNumber: user.phoneNumber,
        status: user.status,
        date: formatDate(Math.floor(user.joinedAt / 1000)),
        applicationRoles: user.applicationRoles,
        userStatus: user.userStatus,
        userId: user.userId,
        picture: user.picture || ''
    }));


    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const editDialogOpen = () => {
        setDialogOpen(true);
    };

    const editDialogClose = () => {
        setDialogOpen(false);
    };

    const handleConfirm = () => {
        fetchUsers();
        setDialogOpen(false);
    };

    const handleChangePasswordConfirm = () => {
        setChangePasswordDialog(false);
    };


    const handleDialogLicenseOpen = () => {
        setDialogOpenLicnese(true);
    };

    const handleDialogLicenseClose = () => {
        setDialogOpenLicnese(false);
    };

    const handleChangePagination = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const [open, setOpen] = useState(false);

    const toggleDrawer = (status: boolean, user?: CompanyUser) => () => {
        setOpen(status);
        if (status && user) {
            setSelectedUser(user);
        } else {
            setSelectedUser(null);
        }
    };

    // tabs
    const [tabValue, setTabValue] = useState<number>(0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Move fetchUsers to a separate function so it can be reused
    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            
            // Security: Validate user permissions using utility function
            const permissionCheck = validateUserPermissions('OWNER');
            if (!permissionCheck.isValid) {
                throw new Error(permissionCheck.error);
            }
            
            const companyId = permissionCheck.companyId!;
            
            // Security: Additional validation of company ID format
            const companyIdValidation = validateCompanyId(companyId);
            if (!companyIdValidation.isValid) {
                throw new Error(companyIdValidation.error);
            }
            
            const response = await AuthService.getCompanyUsers(companyId);
            setUsers(response.data.users);
        } catch (err: any) {
            console.error('Error fetching users:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Call fetchUsers on component mount
    useEffect(() => {
        fetchUsers();
    }, []);

    // Clear search query when AddUser dialog opens to prevent autofill
    useEffect(() => {
        if (dialogOpen) {
            setSearchQuery('');
            // Reset readonly attribute to prevent autofill
            if (searchInputRef.current) {
                searchInputRef.current.setAttribute('readonly', 'true');
            }
        }
    }, [dialogOpen]);

    // Filter users based on search query
    const filteredRows = rows.filter(row => {
        const searchTerm = searchQuery.toLowerCase();
        return (
            (row.name?.toLowerCase() || '').includes(searchTerm) ||
            (row.email?.toLowerCase() || '').includes(searchTerm) ||
            (row.phoneNumber?.toLowerCase() || '').includes(searchTerm)
        );
    });

    // Use filteredRows instead of rows for pagination
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRows = filteredRows.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filteredRows.length / itemsPerPage);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchQuery(value);
        setPage(1); // Reset to first page when searching
    };

    // Handle focus to remove readonly attribute (prevents autofill)
    const handleSearchFocus = () => {
        if (searchInputRef.current) {
            searchInputRef.current.removeAttribute('readonly');
        }
    };

    return (
        <>
            <Container sx={{ maxWidth: "1434px !important", px: "0 !Important" }}>
                <Box mb="52px">
                    <Box>
                        <Typography color="#333333" fontSize="24px" mb="15px" fontWeight="500">
                            Team
                        </Typography>
                        <Typography>Manage your team members</Typography>
                    </Box>
                    {isPausedPlan ? (
                        <Box mb={4} mt={3}>
                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius: '8px',
                                    boxShadow: '0px 4px 12px 0px #2B2B300F',
                                    p: 3,
                                    backgroundColor: '#FFF8E6',
                                    color: '#333333',
                                    border: '1px solid #FFE082',
                                    '& .MuiAlert-icon': {
                                        color: '#F9A825'
                                    }
                                }}
                            >
                                <Typography variant="h6" mb={1} fontWeight="500">
                                    Team Management Unavailable
                                </Typography>
                                <Typography mb={2}>
                                    The Team feature is not available while your subscription is paused. Resume your subscription to access this feature and manage your team members.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disableElevation
                                    href="/dashboard/subscription"
                                    sx={{
                                        textTransform: "capitalize",
                                        px: "24px",
                                        height: "40px",
                                        fontSize: "16px",
                                        borderRadius: "8px",
                                        minWidth: "fit-content",
                                        "&:hover": {
                                            border: "none !important",
                                            bgcolor: "#066fcffa",
                                        }
                                    }}
                                >
                                    Resume Subscription
                                </Button>
                            </Alert>
                        </Box>
                    ) : (
                    <>
                    {/* <Box sx={{
                        display: 'none',
                        '@media (max-width: 1500px)': {
                            display: 'block'
                        }
                    }}>
                        <Box width="100%" display="flex" marginTop="20px" gap="16px" flexWrap="wrap" justifyContent={{ xs: "center", sm: "unset" }}>
                            <Box width="100%" maxWidth="163px">
                                <Box display="flex" justifyContent="space-between" marginBottom="8px">
                                    <Box display="flex">
                                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easywebinar-logo.png" alt="easy-webinar-logo" />
                                        <Typography color='#333' variant='body2' fontWeight="500" ml="9px">EasyWebinar</Typography>
                                    </Box>
                                    <Typography color="#666" fontSize="14px">7/10</Typography>
                                </Box>
                                <BorderLinearProgressMobile variant="determinate" value={70} sx={{ marginBottom: "8px" }} />
                            </Box>
                            <BorderLinearProgressMobile variant="determinate" value={70} sx={{ marginBottom: "8px" }} />
                            {isEasyCRM && <Box width="100%" maxWidth="163px">
                                <Box display="flex" justifyContent="space-between" marginBottom="8px">
                                    <Box display="flex">
                                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easycrm-logo.png" />
                                        <Typography color='#333' variant='body2' fontWeight="500" ml="9px">EasyCRM</Typography>
                                    </Box>
                                    <Typography color="#666" fontSize="14px">7/10</Typography>
                                </Box>
                                <BorderLinearProgressMobile variant="determinate" value={70} sx={{ marginBottom: "8px" }} />
                            </Box>}
                            <Box maxWidth={{xs:"100%" ,sm:"228px"}} width="100%">
                                <Box display="flex" alignItems="flex-start" sx={{ background: "#FEF9F1" }} padding="4px 6px">
                                    <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/alert-fill.svg" alt="alert-icon" />
                                    <Typography marginLeft="4px" color="#333333" fontSize={{xs:"16px" ,sm:"12px"}}>To add more users to your account, please <Typography display="inline-block" sx={{ textDecoration: "underline", fontSize: "12px" }}><a href="" style={{ color: "#178EFC" }} onClick={handleDialogLicenseOpen}>purchase more licenses</a></Typography></Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box> */}
                    <Box display="flex" gap="12px">
                        <Box className='settings-module-listing' width="100%">
                            <Box mb="12px" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" marginTop="12px" gap="14px">
                                <Box width={{ xs: "100%", sm: "unset" }}>
                                    <Search sx={{ background: "#FFFFFF", "&:hover": { background: "#FFFFFF" }, ml: "auto !important", minWidth: "150px" }}>
                                        <SearchIconWrapper>
                                            <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/search-normal.svg" alt="search-icon" />
                                        </SearchIconWrapper>
                                        <InputBase
                                            placeholder="Search by name, email or phone..."
                                            value={searchQuery}
                                            onChange={handleInputChange}
                                            onFocus={handleSearchFocus}
                                            inputRef={searchInputRef}
                                            name="search-users-input"
                                            autoComplete="chrome-off"
                                            data-form-type="other"
                                            type="search"
                                            inputProps={{
                                                autoComplete: "off",
                                                autoCapitalize: "off",
                                                spellCheck: "false",
                                                readOnly: true,
                                            }}
                                            sx={{
                                                color: 'inherit',
                                                width: '100%',
                                                '& .MuiInputBase-input': {
                                                    padding: '8px',
                                                    paddingLeft: '40px',
                                                    width: '100%',
                                                    '@media (min-width: 900px)': {
                                                        width: '354px',
                                                    },
                                                    border: '1px solid #E8EAF0',
                                                    borderRadius: '8px',
                                                },
                                            }}
                                        />
                                    </Search>
                                </Box>

                                <Box display="flex" justifyContent="space-between" gap="12px" width={{ xs: "100%", sm: "unset" }}>
                                    {/* <Button
                                        variant="outlined"
                                        sx={{ textTransform: "capitalize", minWidth: "fit-content", color: "#2B2B30", borderRadius: "8px", border: "1px solid #E8EAF0", px: "24px", background: "#fff" }}
                                        onClick={handleDialogLicenseOpen}
                                    >
                                        Buy Licenses
                                    </Button> */}
                                    <BuyLicense onClose={handleDialogLicenseClose} open={dialogOpenLicense} />

                                    <Button
                                        onClick={handleDialogOpen}
                                        sx={{
                                            textTransform: "capitalize",
                                            bgcolor: "#178EFC",
                                            color: "#FFFFFF !important",
                                            px: "24px",
                                            height: "40px",
                                            fontSize: "16px",
                                            minWidth: "fit-content",
                                            borderRadius: "8px",
                                            "&:hover": {
                                                border: "none !important",
                                                bgcolor: "#066fcffa",
                                            },
                                            "&.Mui-disabled": {
                                                bgcolor: "#E8EAF0",
                                                color: "#8F8F8F !important",
                                            },
                                        }}
                                        disabled={false}
                                    >
                                        Add User
                                    </Button>
                                    <AddUser onConfirm={handleConfirm} onClose={handleDialogClose} open={dialogOpen} />
                                </Box>
                            </Box>

                            <TableContainer component={Paper} sx={{ boxShadow: '0px 4px 12px 0px #2B2B300F' }}>
                                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Users</TableCell>
                                            <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400", minWidth: "167px" }}>Phone Number</TableCell>
                                            <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', width: '20%', fontWeight: "400" }}>Licenses & Roles</TableCell>
                                            <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400", minWidth: "140px" }}>Creation Date</TableCell>
                                            <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    {isLoading ? (
                                        <TableBody>
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ border: 'none', py: 3 }}>
                                                   <Loader fullHeight minHeight='200px' gifSize={50}/>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    ) : error ? (
                                        <TableBody>
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ border: 'none', py: 3 }}>
                                                    <Typography color="error">{error}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    ) : filteredRows.length === 0 ? (
                                        <TableBody>
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ border: 'none', py: 3 }}>
                                                    <Typography color="textSecondary">
                                                        {searchQuery ? 'No users found matching your search' : 'No users found'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    ) : (
                                        <TableBody>
                                            {paginatedRows.map((row) => (
                                                <TableRow
                                                    key={row.userId}
                                                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: "#178efc0a" }, cursor: 'pointer' }}
                                                >
                                                    <TableCell align="left" component="th" scope="row" sx={{ color: '#231F1F', fontSize: '18px', borderBottom: '1px solid #E8EAF0' }}>
                                                        <Box display="flex" alignItems="center" gap="10px"
                                                            onClick={toggleDrawer(true, users.find(u => u.userId === row.userId))}>
                                                            <Avatar alt={row.name} src={row.picture} />
                                                            <Box>
                                                                <Typography fontSize="16px" textTransform="capitalize">
                                                                    {row.name}
                                                                </Typography>
                                                                <Typography variant='body2'>{row.email}</Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="left" sx={{ color: '#231F1F', borderBottom: '1px solid #E8EAF0', fontSize: '16px', minWidth: "167px" }}>{row.phoneNumber}</TableCell>
                                                    <TableCell align="left" sx={{ color: '#231F1F', fontSize: '16px', borderBottom: '1px solid #E8EAF0' }}>
                                                        <Box display="flex">
                                                            {Object.entries(row.applicationRoles)
                                                                .filter(([app]) => app !== "EASYCRM") // Filter out EasyCRM as per requirement
                                                                .map(([app, role]) => {
                                                                    return (
                                                                        <Box key={app} marginRight="20px" display="flex">
                                                                            <img src={`https://easywebinar-site.easywebinar.com/images/autheticator-app-images/${app.toLowerCase()}-logo.png`} alt={`${app}-logo`} />
                                                                            <Button
                                                                                variant="outlined"
                                                                                sx={{
                                                                                    borderRadius: "4px",
                                                                                    textTransform: "capitalize",
                                                                                    border: app === 'EASYWEBINAR' ? "1px solid #22B060" : "1px solid #614EFA",
                                                                                    background: app === 'EASYWEBINAR' ? "rgba(34, 176, 96, 0.09)" : "rgba(97, 78, 250, 0.09)",
                                                                                    color: app === 'EASYWEBINAR' ? "#22B060" : "#614EFA",
                                                                                    fontSize: "12px",
                                                                                    height: "26px",
                                                                                    marginLeft: "8px"
                                                                                }}
                                                                            >
                                                                                {role}
                                                                            </Button>
                                                                        </Box>
                                                                    )
                                                                })}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="left" sx={{ color: '#231F1F', fontSize: '16px', borderBottom: '1px solid #E8EAF0' }}>{row.date}</TableCell>
                                                    <TableCell align="left" sx={{ cursor: 'pointer' }}>
                                                        <Box display="flex" gap="10px" className='user-table-action'>
                                                            <Tooltip title="This will be available soon" arrow>
                                                                <div>
                                                                    <Box 
                                                                        width="28px" 
                                                                        height="28px" 
                                                                        borderRadius="4px" 
                                                                        border="1px solid #E8EAF0" 
                                                                        padding="2px" 
                                                                        display="flex" 
                                                                        sx={{ 
                                                                            cursor: 'not-allowed', 
                                                                            opacity: 0.6
                                                                        }}
                                                                    >
                                                                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/edit-btn.svg" alt="edit-btn" />
                                                                    </Box>
                                                                </div>
                                                            </Tooltip>
                                                            <Tooltip title="This will be available soon" arrow>
                                                                <div>
                                                                    <Box 
                                                                        width="28px" 
                                                                        height="28px" 
                                                                        borderRadius="4px" 
                                                                        border="1px solid #E8EAF0" 
                                                                        padding="2px" 
                                                                        display="flex" 
                                                                        sx={{ 
                                                                            cursor: 'not-allowed', 
                                                                            opacity: 0.6
                                                                        }}
                                                                    >
                                                                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/delete-btn.svg" alt="delete-outline-btn" />
                                                                    </Box>
                                                                </div>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    )}
                                </Table>
                            </TableContainer>
                            {!isLoading && !error && filteredRows.length > 0 && (
                                <Box
                                    display="flex"
                                    justifyContent={{ xs: "center", sm: "space-between" }}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap="12px"
                                    marginTop={{ xs: "32px", sm: "52px" }}
                                    sx={{ xs: { marginBottom: "24px" }, sm: { marginBottom: "0px" } }}
                                    flexDirection={{ xs: "column", sm: "row" }}
                                >
                                    <Typography sx={{ marginX: 2 }} color='#2B2B30'>
                                        {`${startIndex + 1}-${Math.min(endIndex, filteredRows.length)} of ${filteredRows.length} records`}
                                    </Typography>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handleChangePagination}
                                        variant="outlined"
                                        shape="rounded"
                                        sx={{
                                            paddingTop: "0 !important",
                                            overflowY: 'unset !important',
                                            "& button": {
                                                background: "#FFFFFF",
                                                boxShadow: "0px 0.929px 0.929px 0px rgba(0, 0, 0, 0.20), 0px 1.859px 1.859px 0px rgba(0, 0, 0, 0.14), 0px 0.929px 4.647px 0px rgba(0, 0, 0, 0.12)",
                                                border: "none"
                                            },
                                            "& .Mui-selected": {
                                                backgroundColor: "#178EFC !important",
                                                color: "#FFFFFF",
                                            },
                                            "button": {
                                                minWidth: { xs: "27px", sm: "32px" },
                                            },
                                        }}
                                    />
                                    <Box width="13%"></Box>
                                </Box>
                            )}
                        </Box>
                        {/* <Box
                            sx={{
                                maxWidth: "276px",
                                background: "#fff",
                                borderRadius: "8px",
                                border: "1px solid #E8EAF0",
                                boxShadow: "0px 4px 12px 0px rgba(43, 43, 48, 0.06)",
                                padding: "24px",
                                marginTop: "66px",
                                height: "fit-content",
                                display: "block",
                                "@media (max-width: 1500px)": {
                                    display: "none",
                                },
                            }}
                        >
                            <Typography color="#333" fontSize="18px" fontWeight="500" marginBottom="20px">Licenses Used</Typography>
                            <Box marginBottom="2px">
                                <Box>
                                    <Box display="flex" justifyContent="space-between" marginBottom="8px">
                                        <Box display="flex">
                                            <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easywebinar-logo.png" alt="easy-webinar-logo" />
                                            <Typography color='#333' variant='body2' fontWeight="500" ml="9px">EasyWebinar</Typography>
                                        </Box>
                                        <Typography color="#666" fontSize="14px">7/10</Typography>
                                    </Box>
                                    <BorderLinearProgress variant="determinate" value={70} sx={{ marginBottom: "8px" }} />
                                    <Typography color="#333" fontSize="12px" marginBottom="8px">
                                        Host live and automated webinars effortlessly with EasyWebinar—your all-in-one solution for seamless streaming, engagement, and growth.
                                    </Typography>
                                    <Typography variant="body2" onClick={handleDialogLicenseOpen} color="#178EFC" sx={{ textDecoration: "underline", marginBottom: "32px", cursor: "pointer" }}><a href=""></a>Get more licenses</Typography>
                                    <Box>
                                    </Box>
                                </Box>
                            </Box>
                            {isEasyCRM && <Box>
                                <Box>
                                    <Box display="flex" justifyContent="space-between" marginBottom="8px">
                                        <Box display="flex">
                                            <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easycrm-logo.png" />
                                            <Typography color='#333' variant='body2' fontWeight="500" ml="9px">EasyCRM</Typography>
                                        </Box>
                                        <Typography color="#666" fontSize="14px">7/10</Typography>
                                    </Box>
                                    <BorderLinearProgress variant="determinate" value={70} sx={{ marginBottom: "8px" }} />
                                    <Typography color="#333" fontSize="12px" marginBottom="8px">
                                        Streamline your sales, marketing, and customer relationships with EasySuiteCRM—an all-in-one CRM solution for efficient business management.
                                    </Typography>
                                </Box>
                                <Box>
                                    <Box display="flex" alignItems="flex-start" sx={{ background: "#FEF9F1" }} padding="4px 6px">
                                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/alert-fill.svg" alt="alert-icon" />
                                        <Typography marginLeft="4px" color="#333333" fontSize="12px">To add more users to your account, please <Typography display="inline-block" sx={{ textDecoration: "underline", fontSize: "12px" }}><a href="" style={{ color: "#178EFC" }} onClick={handleDialogLicenseOpen}>purchase more licenses</a></Typography></Typography>
                                    </Box>
                                </Box>
                            </Box>}
                        </Box> */}
                    </Box>
                </>
                    )}
                </Box>
            </Container>
            {/* User Info html sidebar */}
            <Backdrop
                open={open}
                onClick={toggleDrawer(false)}
                sx={{ backgroundColor: "rgba(0, 0, 0, 0.2)", zIndex: 1200 }}
            />

            {/* Sidebar Drawer */}
            <Drawer
                anchor="right"
                open={open}
                onClose={toggleDrawer(false)}
                transitionDuration={300}
                className='user-info-box'
                sx={{
                    "& .MuiDrawer-paper": {
                        maxWidth: "570px",
                        width: "100%",
                        height: "calc(100% - 65px)",
                        transition: "transform 0.3s ease-in-out",
                    },
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        p: 2,
                    }}
                >
                    {/* Sidebar Content */}
                    <Box
                        sx={{
                            width: "100%"
                        }}
                    >
                        <IconButton
                            aria-label="close"
                            onClick={toggleDrawer(false)}
                            sx={() => ({
                                position: 'absolute',
                                right: { xs: "17px" },
                                top: { xs: "15px" },

                            })}>
                            <ModalClose></ModalClose>
                        </IconButton>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                padding: 2,
                                borderRadius: 2
                            }}
                        >
                            {/* Profile Image */}
                            <Stack direction="column" alignItems="center">
                                <Avatar
                                    src={selectedUser?.picture}
                                    alt={selectedUser?.name}
                                    sx={{ width: 100, height: 100, marginBottom: 1 }}
                                />

                                {/* Change Image Link */}
                                {/* <Link
                                    component="button"
                                    sx={{ color: "#178EFC", textDecoration: "none", cursor: "pointer", mb: 2, fontSize: "14px" }}
                                >
                                    Change Photo
                                </Link> */}
                            </Stack>

                            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, ml: 2, marginTop: "-50px" }}>

                                {/* User Name */}
                                <Typography variant="h6" fontWeight="bold" textTransform="capitalize">
                                    {selectedUser?.name}
                                </Typography>

                                {/* Buttons Section */}
                                <Box mt={2} width="100%"
                                    sx={{
                                        display: "flex",
                                        gap: "20px",
                                        marginTop: "5px"
                                    }}
                                >
                                    <Link
                                        onClick={() => setChangePasswordDialog(true)}
                                        component="button"
                                        sx={{ color: "#666666", textDecoration: "none", cursor: "pointer", mb: 2, fontSize: "14px", marginBottom: "0", display: "flex", alignItems: "center", gap: "4px" }}>
                                        <PasswordIconBlue />
                                        Change Password
                                    </Link>

                                    <ChangePasswordModal
                                        open={changePasswordDialog}
                                        onClose={() => setChangePasswordDialog(false)}
                                        title="Change Password"
                                        content="Update your password securely."
                                        onConfirm={handleChangePasswordConfirm}
                                        username={selectedUser?.email}
                                    />

                                    {/* <Link
                                        component="button"
                                        sx={{ color: "#666666", textDecoration: "none", cursor: "pointer", mb: 2, fontSize: "14px", marginBottom: "0", display: "flex", alignItems: "center", gap: "4px" }}>
                                        <DeleteIconBlue />
                                        Delete User
                                    </Link> */}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* tabs */}
                    <Box sx={{ width: "100%", maxWidth: 600, mt: 3, marginTop: "0" }} className='user-info-tabs'>
                        {/* Tabs */}
                        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                            <Tab label="User Info" sx={{ textTransform: "capitalize" }} />
                        </Tabs>

                        {/* Tab Content */}
                        <Box sx={{ mt: 2, padding: "0 15px 35px", marginTop: "35px" }}>
                            {tabValue === 0 && (
                                <>
                                    <Box>
                                        <Grid container columnSpacing={2} rowSpacing={{ xs: 5 }}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Box>
                                                    <Typography variant="body1" mb="8px">First Name</Typography>
                                                    <Typography variant="body1" color="textSecondary" fontWeight="normal" textTransform="capitalize">
                                                        {selectedUser?.name.split(' ')[0]}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Box>
                                                    <Typography variant="body1" mb="8px">Last Name</Typography>
                                                    <Typography variant="body1" color="textSecondary" fontWeight="normal" textTransform="capitalize">
                                                        {selectedUser?.name.split(' ').slice(1).join(' ')}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Box sx={{ wordBreak: "break-all" }}>
                                                    <Typography variant="body1" mb="8px">Email</Typography>
                                                    <Typography variant="body1" color="textSecondary" fontWeight="normal">
                                                        {selectedUser?.email}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Box>
                                                    <Typography variant="body1" mb="8px">Phone Number</Typography>
                                                    <Typography variant="body1" color="textSecondary" fontWeight="normal">
                                                        {selectedUser?.phoneNumber}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Box>
                                                    <Typography variant="body1" mb="8px">Role</Typography>
                                                    <Typography variant="body1" color="textSecondary" fontWeight="normal">
                                                        {selectedUser?.applicationRoles ? Object.values(selectedUser.applicationRoles)[0] : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            {/* <Grid size={{ xs: 12 }}>
                                                <Typography variant="h6" sx={{ fontSize: "16px", color: "#333333", marginBottom: "10px" }}>
                                                    Licenses ({Object.keys(selectedUser?.applicationRoles || {}).length})
                                                </Typography>
                                                <Grid container rowSpacing={{ xs: 3, sm: 3 }}>
                                                    {Object.entries(selectedUser?.applicationRoles || {}).map(([app, role]) => (
                                                        <>
                                                            <Grid key={app}>
                                                                <Box display="flex">
                                                                    <Box mr="31px" display="inline-block">{app === 'EASYWEBINAR' ? (<img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/EasyWebinarLogo.svg" alt="easywebinar-logo" />) : <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/EasyCrmLogo.svg" alt="EasyCrmLogo-logo" />}
                                                                        <Typography variant="body2" fontSize="12px" color="textSecondary">
                                                                            Role: {role}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Grid>
                                                        </>
                                                    ))}
                                                </Grid>
                                            </Grid> */}
                                        </Grid>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};
export default Users;
